/**
 * MapLabels
 * Renders place markers, labels, clusters, zoom zones, and unit labels as HTML elements
 * This provides crisp rendering at any zoom level
 */

import React, {
  useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useAppStore, useMapStateStore } from '../../../stores';
import { useFavoriteButtons } from '../../Personalization/Favorite/FavoriteButtonsContext';
import { svgToPercent, getCenterFromPlace } from './MapLabelsHelpers';
import MapPlaceLabel from '../MapPlace/MapPlaceLabel';
import MapPlaceCluster from '../MapPlace/MapPlaceCluster';
import MapUnitLabel from '../MapUnit/MapUnitLabel';
import MapZoomZone from '../MapZoomZone/MapZoomZone';
import useZoneLabelCollision from './useZoneLabelCollision';

/**
 * Main HTML Overlay Component
 */
const MapLabels = ({
  places = [],
  clusters = [],
  zoomZones = {},
  units = [],
  unitsVersion = 0,
  unclusteredPlaces = [],
  clusteringEnabled = false,
  showZoomZones = false,
  zoomZonesOnFloor = false,
  showUnitLabels = false,
  showPlaceLabels = true,
  platformShowOccupancy = false,
  platformShowMapPlaceLabelsInitially = false,
  platformShowLowerValuePlaceLabelsInitially = false,
  isZoomZonesGuideActive = false,
  isAmenitiesGuideActive = false,
  viewBox,
  // uuid → { minX, minY, maxX, maxY } bbox in SVG coords, from
  // MapOverlayLabels. Bounds the collision pass's step 3 marker shift.
  zoneFootprints = null,
  activeZoom,
  baseZoom = 18,
  onMarkerClick,
  onClusterClick,
  onZoomZoneClick,
}) => {
  // Track which non-link labels are visible
  const [visibleLabels, setVisibleLabels] = useState({});

  const containerRef = useRef(null);

  // Get personalization settings from Zustand
  const personalizationActive = useAppStore((state) => state.personalizationActive);
  const settings = useAppStore((state) => state.settings);
  const { platform } = settings || {};
  const tgaiSessionActive = useMapStateStore((state) => state.tgaiSessionActive);
  const previewGranted = useAppStore((state) => state.previewGranted);

  // Get raw favorites data for pre-computing favorited UUIDs
  const { favorites, residenceFavorites } = useFavoriteButtons();

  // Heart icons show when personalization is active, on page platform, or during
  // an active TourGuide AI session (prospects favorite amenities mid-tour), and
  // the item is favorited.
  const showHeartEnabled = personalizationActive || platform === 'page' || tgaiSessionActive;

  // Pre-compute favorited UUIDs as Sets for O(1) lookup
  const favoritedUnitUuids = useMemo(() => {
    if (!showHeartEnabled) return new Set();
    return new Set(residenceFavorites?.residence || []);
  }, [showHeartEnabled, residenceFavorites?.residence]);

  const favoritedPlaceUuids = useMemo(() => {
    if (!showHeartEnabled) return new Set();
    return new Set(favorites?.place || []);
  }, [showHeartEnabled, favorites?.place]);

  const toggleLabel = useCallback((uuid) => {
    setVisibleLabels((prev) => ({
      ...prev,
      [uuid]: !prev[uuid],
    }));
  }, []);

  // Determine which places to render (clustered vs unclustered)
  const candidatePlaces = clusteringEnabled ? unclusteredPlaces : places;

  // A draft place gets no marker at all on the public map (#552). Drupal
  // already strips the name and the modal from an unpublished pin outside
  // preview, so this is what stops the bare, clickable dot that was left
  // behind. In preview the marker stays and MapPlaceLabel badges it "Draft".
  const placesToRender = useMemo(() => {
    if (previewGranted === true) return candidatePlaces;
    return candidatePlaces.filter((place) => place?.pin?.unpublished !== true);
  }, [candidatePlaces, previewGranted]);

  // Seed zoom zone labels to "visible" so the user can minimize them via a
  // click toggle (mirroring non-link place labels). Only `--asDot` zones use
  // this toggle; full zoom zones ignore the state and always render their
  // label.
  useEffect(() => {
    if (!zoomZones) return;
    setVisibleLabels((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.values(zoomZones).forEach((zone) => {
        const key = `zone:${zone.uuid || zone.tid || zone.id}`;
        if (next[key] === undefined) {
          next[key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [zoomZones]);

  // Seed initial place label visibility from feature flags.
  // "Show Map Place Labels Initially" controls link labels (places with modals/media).
  // "Show Lower Value Map Place Labels Initially" controls non-link labels (places without modals).
  // Link labels (create_modal, link_url, modal_nid) always render via the isLink check in JSX,
  // so the seeding here only affects non-link places.
  // Merges into existing state so user-toggled labels aren't lost on re-renders.
  useEffect(() => {
    if (!platformShowMapPlaceLabelsInitially || !placesToRender.length) {
      return;
    }
    if (!platformShowLowerValuePlaceLabelsInitially) return;

    setVisibleLabels((prev) => {
      const next = { ...prev };
      let changed = false;
      placesToRender.forEach((place, index) => {
        const pin = place?.pin || {};
        const isLink = pin.is_link !== false && (pin.link_url || pin.modal_nid || pin.create_modal);
        if (isLink) return;
        const uniqueId = place.uuid || pin.uuid || `place-${index}`;
        // Only seed new labels — never overwrite user-toggled state
        if (next[uniqueId] === undefined) {
          next[uniqueId] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [platformShowMapPlaceLabelsInitially,
    platformShowLowerValuePlaceLabelsInitially,
    placesToRender]);

  // Memoize position calculations
  const markerPositions = useMemo(() => {
    if (!viewBox) return [];

    return placesToRender.map((place, index) => {
      const center = getCenterFromPlace(place);
      if (!center) return null;

      const position = svgToPercent(center.x, center.y, viewBox);
      if (!position) return null;

      const pin = place?.pin || {};
      const isLink = pin.is_link !== false && (pin.link_url || pin.modal_nid || pin.create_modal);

      const uniqueId = place.uuid || pin.uuid || `place-${index}`;

      return {
        place,
        position,
        isLink,
        uniqueId,
      };
    }).filter(Boolean);
  }, [placesToRender, viewBox]);

  const clusterPositions = useMemo(() => {
    if (!clusteringEnabled || !viewBox) return [];

    return clusters.map((cluster) => {
      if (!cluster.center) return null;

      const position = svgToPercent(cluster.center.x, cluster.center.y, viewBox);
      if (!position) return null;

      return { cluster, position };
    }).filter(Boolean);
  }, [clusters, clusteringEnabled, viewBox]);

  const zoomZonePositions = useMemo(() => {
    if (!showZoomZones || !viewBox) return [];

    return Object.entries(zoomZones).map(([key, zone]) => {
      if (!zone.center) return null;

      const position = svgToPercent(zone.center.x, zone.center.y, viewBox);
      if (!position) return null;

      return {
        zoomZone: { ...zone, id: zone.id || key },
        position,
      };
    }).filter(Boolean);
  }, [zoomZones, showZoomZones, viewBox]);

  // Calculate unit label positions
  const unitLabelPositions = useMemo(() => {
    if (!showUnitLabels || !viewBox || !units.length) return [];

    return units
      .filter((unit) => {
        if (unit.displayMode === 'disabled') return false;
        const { x, y } = unit.label || {};
        return x !== undefined && y !== undefined;
      })
      .map((unit) => {
        const { x, y } = unit.label;
        const svgX = parseFloat(x);
        const svgY = parseFloat(y);

        if (Number.isNaN(svgX) || Number.isNaN(svgY)) return null;

        const position = svgToPercent(svgX, svgY, viewBox);
        if (!position) return null;

        const uniqueId = unit.info?.uuid || unit.attributes?.id || `unit-${svgX}-${svgY}`;

        return { unit, position, uniqueId };
      })
      .filter(Boolean);
    // Deliberately partial dep list — see the note above. (The react-hooks plugin
    // is not enabled in .eslintrc, so a disable directive naming it is itself a
    // lint finding; this comment records the intent instead.)
  }, [units, showUnitLabels, viewBox, unitsVersion]);

  // Zoom-zone label collision avoidance. Removed on 1.14 by acd53470 after it
  // failed to converge on cypresscoveliving and crashed the map; restored here
  // behind three termination guards (#726). The hook carries the full
  // rationale, the guards, and the note that this is unit-verified only and
  // still unverified in a browser pending #745.
  //
  // Disabled for bird's-eye zones (zoomZonesOnFloor === false), as it was
  // before removal.
  const collisionEnabled = showZoomZones && zoomZonesOnFloor;

  // Zone label visibility only — toggling an individual PLACE label must not
  // re-run the pass. Sorted so the string is stable under key reordering.
  const zoneVisibilitySignature = Object.entries(visibleLabels)
    .filter(([k]) => k.startsWith('zone:'))
    .map(([k, v]) => `${k}:${v ? 1 : 0}`)
    .sort()
    .join(',');

  // The pass budget resets when this changes, and only when this changes —
  // never on the self-inflicted tick.
  const collisionInputSignature = [
    zoomZonePositions.map(({ zoomZone }) => zoomZone.id).join(','),
    markerPositions.length,
    unitLabelPositions.length,
    clusterPositions.length,
    viewBox ? `${viewBox.x},${viewBox.y},${viewBox.width},${viewBox.height}` : '',
    zoneVisibilitySignature,
  ].join('|');

  const { zoneOrientationOverrides, zoneIconOffsets } = useZoneLabelCollision({
    containerRef,
    enabled: collisionEnabled,
    inputSignature: collisionInputSignature,
    zoneFootprints,
    viewBox,
  });

  // Container styles - must have explicit dimensions for percentage-based positioning
  const containerStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none', // Allow clicks to pass through to SVG below
    overflow: 'visible', // Allow labels to extend beyond bounds
  };

  return (
    <div className="mapLabels" style={containerStyle} ref={containerRef}>

      {/* Render unit labels (bottom layer - behind everything) */}
      {unitLabelPositions.map(({ unit, position, uniqueId }) => (
        <MapUnitLabel
          key={uniqueId}
          unit={unit}
          position={position}
          isFavorited={favoritedUnitUuids.has(unit.info?.uuid)}
          showHeartEnabled={showHeartEnabled}
          showOccupancyInLabel={platformShowOccupancy}
          activeZoom={activeZoom}
          baseZoom={baseZoom}
        />
      ))}

      {/* Render zoom zones */}
      {zoomZonePositions.map(({ zoomZone, position }) => {
        const zoneKey = `zone:${zoomZone.uuid || zoomZone.id}`;
        // --asDot zones default to visible; user toggle stored in visibleLabels.
        // Non-asDot zones always show their label (full zoom navigation hint).
        const labelVisible = !zoomZone.has_active_content
          || visibleLabels[zoneKey] !== false;
        const orientationOverride = zoneOrientationOverrides[zoneKey];
        const iconOffset = zoneIconOffsets[zoneKey];
        return (
          <MapZoomZone
            key={zoomZone.id}
            zoneKey={zoneKey}
            zoomZone={zoomZone}
            position={position}
            onZoomZoneClick={onZoomZoneClick}
            isPulsing={isZoomZonesGuideActive}
            isOnFloor={zoomZonesOnFloor}
            labelVisible={labelVisible}
            onToggleLabel={() => toggleLabel(zoneKey)}
            orientationOverride={orientationOverride}
            iconOffset={iconOffset}
          />
        );
      })}

      {/* Render place markers */}
      {markerPositions.map(({
        place, position, isLink, uniqueId,
      }) => (
        <MapPlaceLabel
          key={uniqueId}
          uniqueId={uniqueId}
          place={place}
          position={position}
          isLink={isLink}
          labelVisible={isLink || visibleLabels[uniqueId]}
          globalLabelVisible={showPlaceLabels}
          onToggleLabel={toggleLabel}
          onMarkerClick={onMarkerClick}
          isFavorited={favoritedPlaceUuids.has(place.pin?.uuid || place.uuid)}
          showHeartEnabled={showHeartEnabled}
          isPulsing={isAmenitiesGuideActive}
        />
      ))}

      {/* Render cluster badges (top layer) */}
      {clusterPositions.map(({ cluster, position }) => (
        <MapPlaceCluster
          key={cluster.id}
          cluster={cluster}
          position={position}
          onClusterClick={onClusterClick}
          globalLabelVisible={showPlaceLabels}
        />
      ))}
    </div>
  );
};

// Place / unit / zone payload objects come straight from the map API and are
// passed through to the child label components, which own their own detailed
// shapes. Typed here as objects rather than restated field by field.
const placeShape = PropTypes.shape({});

MapLabels.propTypes = {
  places: PropTypes.arrayOf(placeShape),
  clusters: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    labels: PropTypes.arrayOf(placeShape),
    center: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
    count: PropTypes.number,
  })),
  // Keyed by `zone:${uuid|tid|index}` — see normalizeZoomZones.
  zoomZones: PropTypes.objectOf(PropTypes.shape({})),
  units: PropTypes.arrayOf(PropTypes.shape({})),
  // Bumped when unit data is replaced in place, to force a re-derive.
  unitsVersion: PropTypes.number,
  unclusteredPlaces: PropTypes.arrayOf(placeShape),
  clusteringEnabled: PropTypes.bool,
  showZoomZones: PropTypes.bool,
  // True when the zones being rendered belong to a map level rather than
  // bird's-eye. Driven by *_show_zoom_zones_on_map_levels.
  zoomZonesOnFloor: PropTypes.bool,
  showUnitLabels: PropTypes.bool,
  showPlaceLabels: PropTypes.bool,
  platformShowOccupancy: PropTypes.bool,
  platformShowMapPlaceLabelsInitially: PropTypes.bool,
  platformShowLowerValuePlaceLabelsInitially: PropTypes.bool,
  isZoomZonesGuideActive: PropTypes.bool,
  isAmenitiesGuideActive: PropTypes.bool,
  viewBox: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  // A Map instance, so shape validation is not available here.
  // eslint-disable-next-line react/forbid-prop-types
  zoneFootprints: PropTypes.object,
  activeZoom: PropTypes.number,
  baseZoom: PropTypes.number,
  onMarkerClick: PropTypes.func,
  onClusterClick: PropTypes.func,
  onZoomZoneClick: PropTypes.func,
};

MapLabels.defaultProps = {
  places: [],
  zoneFootprints: null,
  clusters: [],
  zoomZones: {},
  units: [],
  unitsVersion: 0,
  unclusteredPlaces: [],
  clusteringEnabled: false,
  showZoomZones: false,
  zoomZonesOnFloor: false,
  showUnitLabels: false,
  showPlaceLabels: true,
  platformShowOccupancy: false,
  platformShowMapPlaceLabelsInitially: false,
  platformShowLowerValuePlaceLabelsInitially: false,
  isZoomZonesGuideActive: false,
  isAmenitiesGuideActive: false,
  viewBox: undefined,
  activeZoom: undefined,
  baseZoom: 18,
  onMarkerClick: undefined,
  onClusterClick: undefined,
  onZoomZoneClick: undefined,
};

export default MapLabels;
